import React, { useState, useEffect, useRef, useMemo } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './ui/button';
import { Loader2, X, ClipboardCheck, Eraser, PenLine, Type } from 'lucide-react';

const PACKAGE_TYPES_FILTER = 'compliance_checklist';
export const TRANSACTION_TYPES = ['Purchase', 'Refinance', 'Renewal', 'Switch / Transfer', 'Private Mortgage', 'Construction', 'Reverse Mortgage', 'Other'];

/**
 * ComplianceChecklistModal — full-screen modal that opens for a given client
 * folder. Fetches /defaults, lets the agent edit metadata, tick checklist
 * items, sign (drawn OR typed), then POSTs to create a signed PDF.
 *
 * Props:
 *   apiUrl       - REACT_APP_BACKEND_URL
 *   folder       - the client folder object
 *   agentName    - default agent name (Broker field)
 *   onClose      - close handler
 *   onCreated    - callback after success (refreshes parent packages list)
 */
const ComplianceChecklistModal = ({ apiUrl, folder, agentName, onClose, onCreated }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [form, setForm] = useState({
    broker: agentName || '',
    co_broker: '',
    co_broker_firm: '',
    co_broker_address: '',
    lender: '',
    transaction_type: '',
    velocity_expert_no: '',
    client_names: folder?.client_name || '',
    property_address: '',
    closing_date: '',
    referral_source: '',
    referred_by: '',
    signed_name: agentName || '',
    signature_date: new Date().toISOString().slice(0, 10),
  });
  const [items, setItems] = useState([]);  // [{key,label,checked,file_names?}]
  const [sigMode, setSigMode] = useState('draw'); // 'draw' | 'type'
  const [sigTyped, setSigTyped] = useState(agentName || '');
  const sigPadRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${apiUrl}/api/client-files/${folder.id}/compliance-checklist/defaults`);
        if (r.ok && alive) {
          const d = await r.json();
          setDefaults(d);
          // pre-fill items from detected map; attach file_names that matched
          const detected = d.detected || {};
          const autoTicked = d.auto_ticked || {};
          const fileBlob = (d.uploaded_files || []).map(f => ({
            filename: f.filename, original_filename: f.original_filename, ai: f.ai_suggested_filename,
          }));
          const next = (d.items || []).map(it => {
            const aliases = (it.aliases || []).map(a => a.toLowerCase());
            const file_names = fileBlob
              .filter(f => {
                const hay = [(f.filename || ''), (f.original_filename || ''), (f.ai || '')].join(' ').toLowerCase();
                return aliases.some(a => hay.includes(a));
              })
              .map(f => f.filename);
            return { key: it.key, label: it.label, section: it.section || 'Other', checked: !!detected[it.key], file_names, auto_note: autoTicked[it.key] || null };
          });
          setItems(next);
          if (d.agent?.name) {
            setForm(prev => ({ ...prev, broker: prev.broker || d.agent.name, signed_name: prev.signed_name || d.agent.name }));
            setSigTyped(prev => prev || d.agent.name);
          }
        }
      } catch (e) {
        console.error('Failed to load checklist defaults:', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiUrl, folder.id]);

  const toggleItem = (idx) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it));
  };

  const tickedCount = useMemo(() => items.filter(i => i.checked).length, [items]);

  const handleSubmit = async () => {
    // Validate signature presence
    let signature_drawn_png = null;
    if (sigMode === 'draw') {
      const pad = sigPadRef.current;
      const isEmpty = pad?.isEmpty?.() ?? true;
      if (isEmpty) {
        alert('Please sign in the box (or switch to Type mode).');
        return;
      }
      try {
        signature_drawn_png = pad.getTrimmedCanvas
          ? pad.getTrimmedCanvas().toDataURL('image/png')
          : pad.toDataURL('image/png');
      } catch {
        try { signature_drawn_png = pad.getCanvas().toDataURL('image/png'); } catch (_e) { /* noop */ }
      }
    }
    const signature_typed = sigMode === 'type' ? (sigTyped || '').trim() : null;
    if (sigMode === 'type' && !signature_typed) {
      alert('Please type your signature.');
      return;
    }
    if (!form.signed_name?.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    setSaving(true);
    try {
      const r = await fetch(`${apiUrl}/api/client-files/${folder.id}/compliance-checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          items: items.map(it => ({ key: it.key, label: it.label, checked: it.checked, file_names: it.file_names })),
          signature_typed,
          signature_drawn_png,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        if (onCreated) onCreated(data.package);
        onClose();
      } else {
        const err = await r.json().catch(() => ({}));
        alert(err?.detail || 'Could not generate checklist.');
      }
    } catch (e) {
      console.error('Checklist submit failed:', e);
      alert('Could not generate checklist.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="compliance-checklist-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl my-8 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-[#0B3D7A] to-[#0077B6] text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-bold">File Compliance Checklist</h2>
              <p className="text-xs text-white/80">Client: {folder?.client_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="checklist-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 max-h-[80vh] overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
          ) : (
            <>
              {/* Metadata fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {[
                  ['broker', 'Broker'],
                  ['velocity_expert_no', 'Velocity / Expert No'],
                  ['co_broker', 'Co-Broker'],
                  ['client_names', "Client/s Name/s"],
                  ['co_broker_firm', 'Co-Broker Firm'],
                  ['property_address', 'Property Address'],
                  ['co_broker_address', 'Co-Broker Address'],
                  ['closing_date', 'Closing Date'],
                  ['lender', 'Lender'],
                  ['transaction_type', 'Type of Transaction'],
                ].map(([k, label]) => (
                  <div key={k}>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
                    {k === 'transaction_type' ? (
                      <select
                        value={form[k] || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, [k]: e.target.value }))}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6] bg-blue-50/30"
                        data-testid="checklist-field-transaction_type"
                      >
                        <option value="">Select…</option>
                        {TRANSACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    ) : (
                    <input
                      type={k === 'closing_date' ? 'date' : 'text'}
                      value={form[k] || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, [k]: e.target.value }))}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6] bg-blue-50/30"
                      data-testid={`checklist-field-${k}`}
                    />
                    )}
                  </div>
                ))}
              </div>

              {/* Checklist */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-gray-800">Documents Checklist</h3>
                  <span className="text-xs text-gray-500">{tickedCount} of {items.length} ticked • auto-detected from uploads</span>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  {(() => {
                    // Group items by section, preserving order.
                    const groups = [];
                    let currentSection = null;
                    let bucket = null;
                    items.forEach((it, idx) => {
                      const sec = it.section || 'Other';
                      if (sec !== currentSection) {
                        currentSection = sec;
                        bucket = { section: sec, items: [] };
                        groups.push(bucket);
                      }
                      bucket.items.push({ ...it, _idx: idx });
                    });
                    return groups.map((g) => (
                      <div key={g.section}>
                        <div className="px-3 py-1.5 bg-[#EFF3FA] text-[#0B3D7A] text-xs font-bold uppercase tracking-wide border-y border-[#D7DCE6]">
                          {g.section}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
                          {g.items.map((it) => (
                            <label
                              key={it.key}
                              className={`flex items-start gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 transition-colors ${it.checked ? 'bg-emerald-50/60' : ''}`}
                              data-testid={`checklist-item-${it.key}`}
                            >
                              <input
                                type="checkbox"
                                checked={it.checked}
                                onChange={() => toggleItem(it._idx)}
                                className="mt-0.5 h-4 w-4 accent-emerald-600"
                              />
                              <div className="min-w-0 flex-1">
                                <span className="text-sm text-gray-800">{it.label}</span>
                                {it.auto_note && (
                                  <p className="text-[10px] text-[#0077B6] font-semibold" data-testid={`checklist-auto-${it.key}`}>Auto-ticked — {it.auto_note}</p>
                                )}
                                {it.file_names && it.file_names.length > 0 && (
                                  <p className="text-[10px] text-emerald-700 truncate" title={it.file_names.join(', ')}>
                                    ✓ {it.file_names.slice(0, 2).join(', ')}{it.file_names.length > 2 ? '…' : ''}
                                  </p>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Referral */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Referral Source</label>
                  <input
                    type="text" value={form.referral_source}
                    onChange={(e) => setForm(prev => ({ ...prev, referral_source: e.target.value }))}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                    data-testid="checklist-field-referral_source"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Referred by</label>
                  <input
                    type="text" value={form.referred_by}
                    onChange={(e) => setForm(prev => ({ ...prev, referred_by: e.target.value }))}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                    data-testid="checklist-field-referred_by"
                  />
                </div>
              </div>

              {/* Declaration */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 leading-relaxed mb-5">
                I declare that I have done all due diligence pertaining to knowing the client,
                referral source, verifying all received documents and disclosing all material
                facts that leads to the approval of the mortgage. I confirm that I have uploaded
                all the documents on Velocity and have disposed it as soon as this file has closed.
              </div>

              {/* Signature */}
              <div className="border rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-bold text-gray-800">Agent Signature</label>
                  <div className="flex items-center gap-1 bg-gray-100 rounded-md p-1">
                    <button
                      type="button"
                      onClick={() => setSigMode('draw')}
                      className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${sigMode === 'draw' ? 'bg-white shadow text-[#0077B6]' : 'text-gray-600'}`}
                      data-testid="sig-mode-draw"
                    >
                      <PenLine className="h-3 w-3" /> Draw
                    </button>
                    <button
                      type="button"
                      onClick={() => setSigMode('type')}
                      className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${sigMode === 'type' ? 'bg-white shadow text-[#0077B6]' : 'text-gray-600'}`}
                      data-testid="sig-mode-type"
                    >
                      <Type className="h-3 w-3" /> Type
                    </button>
                  </div>
                </div>
                {sigMode === 'draw' ? (
                  <div>
                    <div className="border border-gray-300 rounded bg-white">
                      <SignatureCanvas
                        ref={sigPadRef}
                        canvasProps={{ width: 700, height: 140, className: 'w-full h-[140px] rounded' }}
                        penColor="#0B3D7A"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => sigPadRef.current?.clear()}
                      className="mt-2 text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
                      data-testid="sig-clear-btn"
                    >
                      <Eraser className="h-3 w-3" /> Clear
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={sigTyped}
                    onChange={(e) => setSigTyped(e.target.value)}
                    placeholder="Type your full name"
                    style={{ fontFamily: '"Great Vibes", "Dancing Script", "Brush Script MT", cursive', color: '#0B3D7A' }}
                    className="w-full px-3 py-4 text-4xl border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                    data-testid="sig-typed-input"
                  />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Printed Name</label>
                    <input
                      type="text"
                      value={form.signed_name}
                      onChange={(e) => setForm(prev => ({ ...prev, signed_name: e.target.value }))}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                      data-testid="checklist-field-signed_name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={form.signature_date}
                      onChange={(e) => setForm(prev => ({ ...prev, signature_date: e.target.value }))}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]"
                      data-testid="checklist-field-signature_date"
                    />
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 italic">
                Note: In order for commissions to be paid, the File Check List must be completed
                &amp; submitted to the Office for every completed/closed file. A copy will also be
                retained in your Client's File.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="checklist-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
            Sign &amp; Generate PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComplianceChecklistModal;
export { PACKAGE_TYPES_FILTER };
